import CryptoJS from 'crypto-js';
import { createContext } from 'react';
import Cookies from 'universal-cookie';

import { AnalyticsConfig } from './AnalyticsConfig';
import AppInsightsPlugin from './AppInsightsPlugin';
import GoogleAnalyticsPlugin from './GoogleAnalyticsPlugin';
import HeapPlugin from './HeapPlugin';
import LogRocketPlugin from './LogRocketPlugin';
import SegmentAnalyticsPlugin from './SegmentAnalyticsPlugin';

declare global {
  interface Window {
    tcsAnalytics: AnalyticsContextType;
  }
}

interface AnalyticsContextType {
  identifyUser(
    userId: string,
    userProperties: Record<string, string | number>
  ): void;
  resetUser(): void;
  trackEvent(category: string, action: string): void;
  trackPageView(path?: string): void;
  isInitialized(): boolean;
}

export type AnalyticsPluginType = AnalyticsContextType;

// Throw error when AnalyticsContext is used outside of context provider
const invariantViolation = () => {
  throw new Error(
    'Attempted to call useAnalytics outside of analytics context. Make sure your app is rendered inside AnalyticsProvider.'
  );
};

export const Analytics = {
  plugins: [] as Array<AnalyticsContextType>,
  identifyUser: (userId, userProperties) => {
    invariantViolation();
  },
  resetUser: () => {
    invariantViolation();
  },
  trackEvent: (category, action) => {
    invariantViolation();
  },
  trackPageView: (path?) => {
    invariantViolation();
  },
  isInitialized: () => {
    invariantViolation();
    return false;
  },
};

export const injectAnalytics = (config: AnalyticsConfig) => {
  const addPluginIfInitialized = (plugin: AnalyticsContextType) => {
    if (plugin.isInitialized()) {
      Analytics.plugins.push(plugin);
    }
  };

  if (config.googleAnalyticsTrackingId) {
    addPluginIfInitialized(
      new GoogleAnalyticsPlugin(config.googleAnalyticsTrackingId)
    );
  }

  if (config.segmentWriteKey) {
    addPluginIfInitialized(new SegmentAnalyticsPlugin(config.segmentWriteKey));
  }

  if (config.heapAppId) {
    addPluginIfInitialized(new HeapPlugin(config.heapAppId));
  }

  if (config.appInsightsInstrumentationKey) {
    addPluginIfInitialized(
      new AppInsightsPlugin(config.appInsightsInstrumentationKey)
    );
  }

  if (config.logRocketAppId) {
    addPluginIfInitialized(new LogRocketPlugin(config.logRocketAppId));
  }

  Analytics.identifyUser = (userId, userProperties) => {
    const cookies = new Cookies();
    const host = window.location.host;
    const allowedHosts = ['forgetrust.com'];
    cookies.set(
      'member_id',
      CryptoJS.SHA256(userId).toString(CryptoJS.enc.Hex),
      {
        domain: allowedHosts.includes(host) ? 'forgetrust.com' : 'localhost',
      }
    );

    Analytics.plugins.forEach((p) => p.identifyUser(userId, userProperties));
  };

  Analytics.resetUser = () => {
    // we want to keep the cookie until another user signs in
    Analytics.plugins.forEach((p) => p.resetUser());
  };

  Analytics.trackEvent = (category, action) => {
    Analytics.plugins.forEach((p) => p.trackEvent(category, action));
  };

  Analytics.trackPageView = (path?) => {
    path = path || window.location.pathname;
    Analytics.plugins.forEach((p) => p.trackPageView(path));
  };

  Analytics.isInitialized = () => true;

  window.tcsAnalytics = Analytics;
};

const AnalyticsContext = createContext<AnalyticsContextType>(Analytics);

export default AnalyticsContext;
